import React, { useCallback, useRef } from 'react';
import { Card, Input, Select, SelectItem } from '@nextui-org/react';
import { Controller, useWatch } from 'react-hook-form';
import { Icon } from '@iconify/react';
import type { ScheduleType } from '@circuitry-ai/doc-data';
import { CtyButton } from '../button/CtyButton';
import {
  RATE_UNIT_OPTIONS,
  STATE_OPTIONS,
  TIMEZONE_OPTIONS,
  ScheduleConfigurationSectionProps,
  scheduleTypeOptions,
} from './scheduleConfigTypes';
import { generateSchedulePreviewText, getScheduleDateInputMin } from './scheduleConfigUtils';

function RateTabContent({
  control,
  setValue,
  handleSelectChange,
}: Readonly<Pick<ScheduleConfigurationSectionProps, 'control' | 'setValue' | 'handleSelectChange'>>) {
  const rateStartDate = useWatch({ control, name: 'rateStartDate' });
  const scheduleDateMin = getScheduleDateInputMin();

  return (
    <div className="space-y-4">
      <div>
        <span className="inline-block pb-2 text-sm font-medium text-default-600">
          Execution Frequency
        </span>
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Controller
              name="rateValue"
              control={control}
              rules={{ required: 'Value is required', min: { value: 1, message: 'Value must be at least 1' } }}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  type="number"
                  placeholder="1"
                  min={1}
                  size="sm"
                  value={field.value?.toString() || ''}
                  onChange={(e) => field.onChange(Number.parseInt(e.target.value, 10) || 0)}
                  isInvalid={!!fieldState.error}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
          </div>
          <div className="flex-1">
            <Controller
              name="rateUnit"
              control={control}
              rules={{ required: 'Unit is required' }}
              render={({ field, fieldState }) => (
                <Select
                  selectedKeys={field.value ? [field.value] : []}
                  onSelectionChange={handleSelectChange(field.onChange)}
                  placeholder="Select unit"
                  size="sm"
                  isInvalid={!!fieldState.error}
                  errorMessage={fieldState.error?.message}
                >
                  {RATE_UNIT_OPTIONS.map((unit) => (
                    <SelectItem key={unit.key} value={unit.value}>
                      {unit.label}
                    </SelectItem>
                  ))}
                </Select>
              )}
            />
          </div>
        </div>
      </div>

      <div>
        <span className="mb-2 block text-sm font-medium text-default-600">
          Start Date <span className="text-xs text-default-400">(optional)</span>
        </span>
        <Controller
          name="rateStartDate"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              type="date"
              size="sm"
              min={scheduleDateMin}
              startContent={<Icon icon="mdi:calendar" className="h-4 w-4 text-default-400" />}
              isInvalid={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              onChange={(e) => {
                field.onChange(e);
                if (!e.target.value) setValue('rateStartTime', '');
              }}
            />
          )}
        />
      </div>

      {rateStartDate ? (
        <div>
          <span className="mb-2 block text-sm font-medium text-default-600">
            Time <span className="text-xs text-default-400">(optional)</span>
          </span>
          <Controller
            name="rateStartTime"
            control={control}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                type="time"
                size="sm"
                isInvalid={!!fieldState.error}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </div>
      ) : null}

      <div>
        <span className="mb-2 block text-sm font-medium text-default-600">
          End Condition <span className="text-xs text-default-400">(optional)</span>
        </span>
        <Controller
          name="rateEndCondition"
          control={control}
          render={({ field }) => (
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  type="radio"
                  id="shared-rate-end-never"
                  checked={field.value === 'never'}
                  onChange={() => field.onChange('never')}
                  className="mr-2"
                />
                <label htmlFor="shared-rate-end-never" className="text-sm text-default-600">
                  Never ends
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="radio"
                  id="shared-rate-end-specific"
                  checked={field.value === 'specific_date'}
                  onChange={() => field.onChange('specific_date')}
                  className="mr-2"
                />
                <label htmlFor="shared-rate-end-specific" className="text-sm text-default-600">
                  End on specific date
                </label>
              </div>
              {field.value === 'specific_date' ? (
                <div className="ml-6 mt-2">
                  <Controller
                    name="rateEndDate"
                    control={control}
                    rules={{ required: 'End date is required' }}
                    render={({ field: endDateField, fieldState: endDateState }) => (
                      <Input
                        {...endDateField}
                        type="date"
                        size="sm"
                        min={scheduleDateMin}
                        isInvalid={!!endDateState.error}
                        errorMessage={endDateState.error?.message}
                      />
                    )}
                  />
                </div>
              ) : null}
            </div>
          )}
        />
      </div>
    </div>
  );
}

function OneTimeTabContent({
  control,
}: Readonly<Pick<ScheduleConfigurationSectionProps, 'control'>>) {
  const scheduleDateMin = getScheduleDateInputMin();
  return (
    <div className="space-y-4">
      <div>
        <span className="mb-2 block text-sm font-medium text-default-600">Date</span>
        <Controller
          name="oneTimeDate"
          control={control}
          rules={{ required: 'Date is required' }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              type="date"
              size="sm"
              min={scheduleDateMin}
              startContent={<Icon icon="mdi:calendar" className="h-4 w-4 text-default-400" />}
              isInvalid={!!fieldState.error}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
      </div>
      <div>
        <span className="mb-2 block text-sm font-medium text-default-600">Time</span>
        <Controller
          name="oneTimeTime"
          control={control}
          rules={{ required: 'Time is required' }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              type="time"
              size="sm"
              isInvalid={!!fieldState.error}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
      </div>
    </div>
  );
}

function ImmediateTabContent() {
  return (
    <p className="text-sm text-default-600">
      You will be notified whenever there is a change happens.
    </p>
  );
}

function RecurringTabContent({
  control,
  handleSelectChange,
  availableRecurringFrequencies,
  repeatEveryFrequencies,
}: Readonly<
  Pick<
    ScheduleConfigurationSectionProps,
    'control' | 'handleSelectChange' | 'availableRecurringFrequencies' | 'repeatEveryFrequencies'
  >
>) {
  const recurringFreq = useWatch({ control, name: 'recurringFrequency' }) || 'DAILY';
  const scheduleDateMin = getScheduleDateInputMin();
  const allDurationOptions = [
    { label: 'Daily', value: 'DAILY' as const },
    { label: 'Weekly', value: 'WEEKLY' as const },
    { label: 'Monthly', value: 'MONTHLY' as const },
  ];
  const durationOptions = availableRecurringFrequencies?.length
    ? allDurationOptions.filter((option) =>
        availableRecurringFrequencies.includes(option.value)
      )
    : allDurationOptions;
  const showRepeatEvery = repeatEveryFrequencies
    ? repeatEveryFrequencies.includes(recurringFreq as 'DAILY' | 'WEEKLY' | 'MONTHLY')
    : recurringFreq === 'DAILY' || recurringFreq === 'MONTHLY';
  const dayOfMonthOptions = [
    { label: 'First day', value: '1' },
    { label: 'Middle of month', value: '15' },
    { label: 'Last day', value: 'l' },
  ];
  const weekDays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const;
  const dayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;

  return (
    <div className="space-y-4">
      <div>
        <span className="mb-2 block text-sm font-medium text-default-600">Repeat Frequency</span>
        <Controller
          name="recurringFrequency"
          control={control}
          rules={{ required: 'Repeat frequency is required' }}
          render={({ field, fieldState }) => (
            <Select
              selectedKeys={field.value ? [field.value] : []}
              onSelectionChange={handleSelectChange(field.onChange)}
              placeholder="Select frequency"
              size="sm"
              isInvalid={!!fieldState.error}
              errorMessage={fieldState.error?.message}
            >
              {durationOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </Select>
          )}
        />
      </div>

      {(recurringFreq === 'DAILY' || recurringFreq === 'MONTHLY') && showRepeatEvery && (
        <div>
          <span className="inline-block pb-2 text-sm font-medium text-default-600">
            Repeat every
          </span>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Controller
                name="recurringRepeatEvery"
                control={control}
                rules={{ required: 'Repeat every is required', min: { value: 1, message: 'Must be at least 1' } }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    type="number"
                    placeholder="1"
                    min={1}
                    size="sm"
                    value={field.value?.toString() || ''}
                    onChange={(e) => field.onChange(Number.parseInt(e.target.value, 10) || 1)}
                    isInvalid={!!fieldState.error}
                    errorMessage={fieldState.error?.message}
                  />
                )}
              />
            </div>
            <span className="pb-1 text-sm text-default-600">
              {recurringFreq === 'DAILY' ? 'day(s)' : 'month(s)'}
            </span>
          </div>
        </div>
      )}

      {recurringFreq === 'WEEKLY' && (
        <div>
          <span className="mb-2 block text-sm font-medium text-default-600">Days of Week</span>
          <Controller
            name="recurringDays"
            control={control}
            rules={{ required: 'At least one day must be selected' }}
            render={({ field, fieldState }) => {
              const selectedDays = field.value || [];
              return (
                <div>
                  <div className="flex gap-2">
                    {weekDays.map((day, index) => {
                      const isSelected = selectedDays.includes(day);
                      return (
                        <CtyButton
                          key={day}
                          size="sm"
                          variant={isSelected ? 'solid' : 'light'}
                          onPress={() => {
                            const newDays = isSelected
                              ? selectedDays.filter((d: string) => d !== day)
                              : [...selectedDays, day];
                            field.onChange(newDays);
                          }}
                          className={`min-w-[40px] ${isSelected ? 'bg-primary text-primary-foreground' : ''}`}
                        >
                          {dayLabels[index]}
                        </CtyButton>
                      );
                    })}
                  </div>
                  {fieldState.error ? (
                    <p className="mt-1 text-xs text-danger">{fieldState.error.message}</p>
                  ) : null}
                </div>
              );
            }}
          />
        </div>
      )}

      {(recurringFreq === 'DAILY' || recurringFreq === 'WEEKLY') && (
        <div>
          <span className="mb-2 block text-sm font-medium text-default-600">
            Start Date <span className="text-xs text-default-400">(optional)</span>
          </span>
          <Controller
            name="recurringStartDate"
            control={control}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                type="date"
                size="sm"
                min={scheduleDateMin}
                startContent={<Icon icon="mdi:calendar" className="h-4 w-4 text-default-400" />}
                isInvalid={!!fieldState.error}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </div>
      )}

      {recurringFreq === 'MONTHLY' && (
        <div>
          <span className="mb-2 block text-sm font-medium text-default-600">Day of Month</span>
          <Controller
            name="recurringDayOfMonth"
            control={control}
            rules={{ required: 'Day of month is required' }}
            render={({ field, fieldState }) => (
              <Select
                selectedKeys={field.value ? [String(field.value)] : []}
                onSelectionChange={(keys) => {
                  const value = Array.from(keys)[0] as string;
                  field.onChange(value === 'l' ? 'l' : Number.parseInt(value, 10));
                }}
                placeholder="Select day"
                size="sm"
                isInvalid={!!fieldState.error}
                errorMessage={fieldState.error?.message}
              >
                {dayOfMonthOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </Select>
            )}
          />
        </div>
      )}

      <div>
        <span className="mb-2 block text-sm font-medium text-default-600">Time</span>
        <Controller
          name="recurringStartTime"
          control={control}
          rules={{ required: 'Start time is required' }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              type="time"
              size="sm"
              isInvalid={!!fieldState.error}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
      </div>

      <div>
        <span className="mb-2 block text-sm font-medium text-default-600">
          End Condition <span className="text-xs text-default-400">(optional)</span>
        </span>
        <Controller
          name="recurringEndCondition"
          control={control}
          render={({ field }) => (
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  type="radio"
                  id="shared-recurring-end-never"
                  checked={field.value === 'never'}
                  onChange={() => field.onChange('never')}
                  className="mr-2"
                />
                <label htmlFor="shared-recurring-end-never" className="text-sm text-default-600">
                  Never ends
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="radio"
                  id="shared-recurring-end-specific"
                  checked={field.value === 'specific_date'}
                  onChange={() => field.onChange('specific_date')}
                  className="mr-2"
                />
                <label htmlFor="shared-recurring-end-specific" className="text-sm text-default-600">
                  End on specific date
                </label>
              </div>
              {field.value === 'specific_date' ? (
                <div className="ml-6 mt-2">
                  <Controller
                    name="recurringEndDate"
                    control={control}
                    rules={{ required: 'End date is required' }}
                    render={({ field: endDateField, fieldState: endDateState }) => (
                      <Input
                        {...endDateField}
                        type="date"
                        size="sm"
                        min={scheduleDateMin}
                        isInvalid={!!endDateState.error}
                        errorMessage={endDateState.error?.message}
                      />
                    )}
                  />
                </div>
              ) : null}
            </div>
          )}
        />
      </div>
    </div>
  );
}

export function ScheduleConfigurationSection({
  control,
  setValue,
  getValues,
  handleSelectChange,
  watchedScheduleType,
  showStateAndTimezone = true,
  availableScheduleTypes,
  availableRecurringFrequencies,
  repeatEveryFrequencies,
  showTimezone = false,
  embedded = false,
}: Readonly<ScheduleConfigurationSectionProps>) {
  const cardRef = useRef<HTMLDivElement>(null);
  const defaultScheduleTypes: ScheduleType[] = [
    scheduleTypeOptions.RATE,
    scheduleTypeOptions.Recurring,
    scheduleTypeOptions.One_time,
  ];
  const visibleScheduleTypes = availableScheduleTypes ?? defaultScheduleTypes;
  const showTimezoneField =
    showTimezone &&
    (watchedScheduleType === scheduleTypeOptions.Recurring ||
      watchedScheduleType === scheduleTypeOptions.One_time);

  const handleTabSwitch = useCallback(
    (type: ScheduleType) => {
      const scrollContainer = cardRef.current?.closest('.overflow-y-auto');
      const savedScroll = scrollContainer?.scrollTop ?? 0;
      setValue('schedule_type', type, { shouldValidate: false });
      requestAnimationFrame(() => {
        if (scrollContainer) scrollContainer.scrollTop = savedScroll;
      });
    },
    [setValue]
  );

  const body = (
    <div className="space-y-4">
      <div>
        <span className="mb-2 block text-xs font-semibold uppercase text-default-500">
          Schedule Type
        </span>
        <div className="flex gap-2 rounded-lg bg-default-100 p-1">
          {visibleScheduleTypes.map((type) => (
            <CtyButton
              key={type}
              size="sm"
              variant={watchedScheduleType === type ? 'solid' : 'light'}
              onPress={() => handleTabSwitch(type as ScheduleType)}
              className={`flex-1 ${watchedScheduleType === type ? 'dark:bg-[var(--cty-bg-hover)]' : ''}`}
            >
              {type}
            </CtyButton>
          ))}
        </div>
      </div>
      {watchedScheduleType === scheduleTypeOptions.RATE && (
        <RateTabContent
          control={control}
          setValue={setValue}
          handleSelectChange={handleSelectChange}
        />
      )}
      {watchedScheduleType === scheduleTypeOptions.Immediate && <ImmediateTabContent />}
      {watchedScheduleType === scheduleTypeOptions.Recurring && (
        <RecurringTabContent
          control={control}
          handleSelectChange={handleSelectChange}
          availableRecurringFrequencies={availableRecurringFrequencies}
          repeatEveryFrequencies={repeatEveryFrequencies}
        />
      )}
      {watchedScheduleType === scheduleTypeOptions.One_time && (
        <OneTimeTabContent control={control} />
      )}
      {showStateAndTimezone ? (
        <div className="mt-4 grid grid-cols-2 gap-4">
          <Controller
            name="state"
            control={control}
            render={({ field }) => (
              <Select
                selectedKeys={field.value ? [field.value] : []}
                onSelectionChange={handleSelectChange(field.onChange)}
                label="State"
                size="sm"
              >
                {STATE_OPTIONS.map((state) => (
                  <SelectItem key={state.key} value={state.value}>
                    {state.label}
                  </SelectItem>
                ))}
              </Select>
            )}
          />
          <Controller
            name="timezone"
            control={control}
            render={({ field }) => (
              <Select
                selectedKeys={field.value ? [field.value] : []}
                onSelectionChange={handleSelectChange(field.onChange)}
                label="Timezone"
                size="sm"
              >
                {TIMEZONE_OPTIONS.map((tz) => (
                  <SelectItem key={tz.key} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </Select>
            )}
          />
        </div>
      ) : null}
      {showTimezoneField ? (
        <Controller
          name="timezone"
          control={control}
          rules={{ required: 'Timezone is required' }}
          render={({ field, fieldState }) => (
            <Select
              selectedKeys={field.value ? [field.value] : []}
              onSelectionChange={handleSelectChange(field.onChange)}
              label="Timezone"
              size="sm"
              isInvalid={!!fieldState.error}
              errorMessage={fieldState.error?.message}
            >
              {TIMEZONE_OPTIONS.map((tz) => (
                <SelectItem key={tz.key} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </Select>
          )}
        />
      ) : null}
      <div className="mt-4 rounded-lg border border-primary bg-primary/10 p-3">
        <p className="text-sm text-primary">{generateSchedulePreviewText(getValues())}</p>
      </div>
    </div>
  );

  if (embedded) {
    return <div ref={cardRef}>{body}</div>;
  }

  return (
    <Card ref={cardRef} className="p-6">
      <h3 className="mb-4 border-b pb-2 text-xl font-medium">Schedule Configuration</h3>
      {body}
    </Card>
  );
}
